import { geoNaturalEarth1, geoMercator, geoPath } from 'd3-geo';
import { useMemo, useState } from 'react';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { Topology } from 'topojson-specification';
import worldTopo from 'world-atlas/countries-110m.json';
import type { GeoPoint } from '../../lib/api/analytics';

/**
 * SVG world map with country choropleth + proportional city bubbles.
 *
 * Replaces the prior Leaflet/raster-tile heatmap. Why hand-rolled SVG:
 *   - Sharp at any DPI / zoom (no blurry tile interpolation).
 *   - Themeable from the admin's design tokens — country fills, bubble
 *     colours, and gridlines all match the editorial palette.
 *   - No external tile request per pan; the GeoJSON is bundled (~110 KB
 *     gzipped for 110m resolution, more than enough at admin-card sizes).
 *   - No attribution clutter, no API-key procurement.
 *
 * Visualisation grammar:
 *   1. Country fill (choropleth) — each country shaded by *total unique
 *      devices that originated from any city inside it*. Quiet countries
 *      stay near the page background; loud ones step toward the brand
 *      colour. This is the "where in the world" answer at a glance.
 *   2. City bubbles — circle per city, area ∝ uniques (Flannery-corrected
 *      sqrt-radius scale so bubbles are visually proportional, not
 *      misleadingly inflated by a linear-radius mapping). This is the
 *      "where exactly" answer.
 *   3. Hover tooltip surfaces city name + country + raw counts.
 */

interface GeoHeatmapProps {
  points: GeoPoint[];
  className?: string;
}

type CountryProps = { name: string };
type CountryFeature = Feature<Geometry, CountryProps>;

const WORLD = (() => {
  const topo = worldTopo as unknown as Topology;
  return feature(topo, topo.objects.countries) as unknown as FeatureCollection<
    Geometry,
    CountryProps
  >;
})();

type ViewKey = 'world' | 'nigeria';

const VIEWS: Record<
  ViewKey,
  {
    label: string;
    /** Returns a fitted projection for the given viewport — d3-geo handles
     *  the sizing, scaling, and centering. */
    project: (width: number, height: number) => d3.GeoProjection;
  }
> = {
  world: {
    label: 'World',
    project: (w, h) =>
      geoNaturalEarth1()
        // Rotate so Africa centres at the meridian — VON's primary audience.
        .rotate([-15, 0, 0])
        .fitSize([w, h], WORLD),
  },
  nigeria: {
    label: 'Nigeria',
    project: (w, h) =>
      geoMercator().fitSize([w, h], findNigeria() ?? WORLD),
  },
};

interface TooltipState {
  x: number;
  y: number;
  city: string;
  country: string | null;
  sessions: number;
  uniques: number;
}

export function GeoHeatmap({ points, className }: GeoHeatmapProps): JSX.Element {
  const [view, setView] = useState<ViewKey>('world');
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  // Aggregate per-country totals for the choropleth. Country lookup is by
  // ISO-A2 from the API; topojson `name` is the English country name, so
  // we hop through a small ISO→name table to match.
  const totalsByCountry = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of points) {
      if (!p.countryIso) continue;
      map.set(p.countryIso, (map.get(p.countryIso) ?? 0) + p.uniques);
    }
    return map;
  }, [points]);

  const maxCountry = useMemo(
    () => Math.max(1, ...totalsByCountry.values()),
    [totalsByCountry],
  );
  const maxCity = useMemo(
    () => Math.max(1, ...points.map((p) => p.uniques)),
    [points],
  );

  // Render-time projection — recomputed on view change. We size on a fixed
  // viewBox so React can hydrate without measuring DOM first; the SVG
  // scales responsively via CSS.
  const VB_W = 960;
  const VB_H = 480;
  const projection = useMemo(
    () => VIEWS[view].project(VB_W, VB_H),
    [view],
  );
  const path = useMemo(() => geoPath(projection), [projection]);

  // Project each city up-front so we can early-skip ones that fall outside
  // the current view (e.g. London disappears when we zoom to Nigeria).
  const projectedCities = useMemo(
    () =>
      points
        .map((p) => {
          const xy = projection([p.lng, p.lat]);
          if (!xy) return null;
          return { p, x: xy[0], y: xy[1] };
        })
        .filter(<T,>(v: T | null): v is T => v !== null),
    [points, projection],
  );

  return (
    <div className={className} style={{ position: 'relative', minHeight: 400 }}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          background:
            'linear-gradient(180deg, var(--colour-surface, #fafafa) 0%, var(--colour-surface-muted, #f4f4f5) 100%)',
          borderRadius: 8,
        }}
        role="img"
        aria-label="Reader-reach world map"
      >
        {/* Country outlines + choropleth fill */}
        <g>
          {WORLD.features.map((f, i) => {
            const iso = isoForCountry(f.properties?.name);
            const total = iso ? totalsByCountry.get(iso) ?? 0 : 0;
            const intensity = total / maxCountry; // 0..1
            const fill = countryFill(intensity);
            const d = path(f as CountryFeature) ?? undefined;
            if (!d) return null;
            return (
              <path
                key={`c-${i}`}
                d={d}
                fill={fill}
                stroke="rgba(15, 23, 42, 0.08)"
                strokeWidth={0.5}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </g>

        {/* City bubbles */}
        <g>
          {projectedCities.map((c, i) => {
            // Flannery-corrected proportional circle: r ∝ sqrt(value), and
            // we apply a sqrt twice over for perceptual accuracy. Capped to
            // avoid massive blobs on giant city centroids.
            const ratio = c.p.uniques / maxCity;
            const r = Math.max(2, Math.min(18, Math.sqrt(ratio) * 18));
            return (
              <g
                key={`p-${i}`}
                transform={`translate(${c.x},${c.y})`}
                onMouseEnter={() =>
                  setTooltip({
                    x: c.x,
                    y: c.y,
                    city: c.p.city ?? '(unknown)',
                    country: c.p.countryIso,
                    sessions: c.p.sessions,
                    uniques: c.p.uniques,
                  })
                }
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Outer halo for separation from busy backgrounds */}
                <circle r={r + 1.5} fill="rgba(220, 38, 38, 0.18)" />
                <circle
                  r={r}
                  fill="rgba(220, 38, 38, 0.65)"
                  stroke="#ffffff"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            );
          })}
        </g>
      </svg>

      {/* View picker (top-right) */}
      <div
        className="absolute right-3 top-3 z-10 inline-flex overflow-hidden rounded-md border border-line bg-surface/95 shadow-sm backdrop-blur"
        role="radiogroup"
        aria-label="Map view"
      >
        {(Object.keys(VIEWS) as ViewKey[]).map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={view === k}
            onClick={() => setView(k)}
            className={
              'px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider transition-colors ' +
              (view === k
                ? 'bg-brand text-white'
                : 'bg-transparent text-ink-muted hover:bg-surface-muted')
            }
          >
            {VIEWS[k].label}
          </button>
        ))}
      </div>

      {/* Hover tooltip — absolutely positioned so it floats above the SVG.
       *  Coordinates come from the city's projected (x, y) inside the SVG's
       *  viewBox, but our parent div is also responsive — so we convert to
       *  percentages of viewBox dimensions which scales correctly. */}
      {tooltip ? (
        <div
          className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs shadow-md"
          style={{
            left: `${(tooltip.x / VB_W) * 100}%`,
            top: `${(tooltip.y / VB_H) * 100}%`,
            marginTop: -8,
          }}
        >
          <div className="font-semibold text-ink">{tooltip.city}</div>
          <div className="text-ink-subtle">
            {tooltip.country ?? '—'} ·{' '}
            <span className="font-mono">{tooltip.uniques.toLocaleString()}</span>{' '}
            devices ·{' '}
            <span className="font-mono">{tooltip.sessions.toLocaleString()}</span>{' '}
            sessions
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ───────────── helpers ─────────────

/** Brand-colour ramp for the country choropleth: pale at zero, deeper toward
 *  the brand red at the dataset's max. Returns a CSS rgba string so the
 *  alpha integrates cleanly with the gradient backdrop. */
function countryFill(intensity: number): string {
  if (intensity <= 0) return 'rgba(212, 212, 216, 0.55)'; // zinc-300 @ 55% — neutral
  // Sqrt-easing so middle values still register visually. Cap alpha at 0.85.
  const eased = Math.sqrt(Math.max(0, Math.min(1, intensity)));
  const alpha = 0.18 + eased * 0.55;
  return `rgba(220, 38, 38, ${alpha.toFixed(3)})`; // red-600
}

/** Subset of the topojson country list — just the ISO-A2 mapping for places
 *  the API can plausibly report. The world-atlas package uses Natural Earth's
 *  English country names; we reverse-map to ISO so the choropleth can match
 *  on `countryIso` from our payload. Extend as new countries appear. */
const NAME_TO_ISO: Record<string, string> = {
  Nigeria: 'NG',
  'United States of America': 'US',
  'United Kingdom': 'GB',
  Canada: 'CA',
  Germany: 'DE',
  France: 'FR',
  Italy: 'IT',
  Spain: 'ES',
  Netherlands: 'NL',
  Belgium: 'BE',
  Switzerland: 'CH',
  Sweden: 'SE',
  Norway: 'NO',
  Ireland: 'IE',
  Australia: 'AU',
  'New Zealand': 'NZ',
  Ghana: 'GH',
  Kenya: 'KE',
  'South Africa': 'ZA',
  Egypt: 'EG',
  Morocco: 'MA',
  Algeria: 'DZ',
  Tunisia: 'TN',
  Senegal: 'SN',
  'Côte d’Ivoire': 'CI',
  Cameroon: 'CM',
  Niger: 'NE',
  Benin: 'BJ',
  Togo: 'TG',
  Mali: 'ML',
  'Burkina Faso': 'BF',
  Chad: 'TD',
  'United Arab Emirates': 'AE',
  'Saudi Arabia': 'SA',
  Qatar: 'QA',
  India: 'IN',
  China: 'CN',
  Japan: 'JP',
  Brazil: 'BR',
};

function isoForCountry(name: string | undefined): string | null {
  if (!name) return null;
  return NAME_TO_ISO[name] ?? null;
}

/** Lookup helper for the Nigeria-zoom view. Falls back to world if Nigeria
 *  isn't in the dataset (shouldn't happen with this TopoJSON, but defensive). */
function findNigeria(): FeatureCollection<Geometry, CountryProps> | null {
  const nigeria = WORLD.features.find((f) => f.properties?.name === 'Nigeria');
  if (!nigeria) return null;
  return { type: 'FeatureCollection', features: [nigeria] };
}

// d3-geo namespace re-export so the type annotation in the VIEWS map resolves
// without dragging the whole d3-geo namespace into the public exports.
import type * as d3 from 'd3-geo';
